(ns app.front.view.attachment.subs
  (:require [app.domain.acceptance.model :as acceptance-model]
            [app.domain.attachment.file-formats :as file-formats]
            [app.domain.util :as du]
            [app.front.acceptance.subs :as acceptance-subs]
            [app.front.entities :as entities]
            [app.front.process :as process]
            [core.db.entity.util :as eu]
            [core.front.event :refer [<sub]]
            [core.user.model :as user-model]
            [core.util.util :as util]
            [re-frame.core-instrumented :as rf]))

;; Subscriptions for attachment versions

(rf/reg-sub ::versions
  :<- [::entities/entities {:entity/type :entity.type/attachment-version}]
  (fn [entities]
    (-> entities
        du/entities->entity-db)))

(rf/reg-sub ::version
  :<- [::versions]
  (fn [versions [_ version-id]]
    {:pre [(integer? version-id)]}
    (get versions version-id)))

(rf/reg-sub ::latest-file-for-version
  (fn [[_ version-id]]
    {:pre [(integer? version-id)]}
    (rf/subscribe [::version version-id]))
  (fn [attachment-version]
    (->> attachment-version
         :attachment.version/files
         (sort-by :entity/created >)
         first)))

(rf/reg-sub ::can-preview?
  (fn [[_ attachment-version-id]]
    (rf/subscribe [::latest-file-for-version attachment-version-id]))
  (fn [attachment-file _]
    (->> attachment-file
         :attachment.version.file/content-type
         keyword
         (contains? file-formats/formats-without-preview)
         not)))

;; Subscriptions for attachments

(rf/reg-sub ::attachments
  :<- [::entities/entities {:entity/type :entity.type/attachment}]
  (fn [entities]
    (-> entities
        du/entities->entity-db)))

(rf/reg-sub ::attachments-for-parent
  :<- [::entities/context]
  (fn [ctx [_ parent-id]]
    {:pre [(integer? parent-id)]}
    (entities/find-entities ctx {:entity/parent parent-id
                                 :entity/type   :entity.type/attachment})))


(rf/reg-sub ::has-attachments?
  (fn [[_ parent-id]]
    {:pre [(integer? parent-id)]}
    (rf/subscribe [::attachments-for-parent parent-id]))
  (fn [all-attachments]
    (boolean (seq all-attachments))))

(rf/reg-sub ::restricted-attachment-ids-for-parent
  (fn [[_ parent-id]]
    {:pre [(integer? parent-id)]}
    (rf/subscribe [::attachments-for-parent parent-id]))
  (fn [attachments]
    (->> attachments
         (filter (comp
                   :permission.attachment/read-restricted
                   :entity/required-read-permissions))
         (map :db/id)
         set)))

(rf/reg-sub ::attachment
  :<- [::attachments]
  (fn [attachments [_ attachment-id]]
    {:pre [(integer? attachment-id)]}
    (get attachments attachment-id)))

;; Subscriptions for attachment attributes

(defn attachment-input-signal [[_ attachment-id]]
  {:pre [(integer? attachment-id)]}
  (rf/subscribe [::attachment attachment-id]))

(rf/reg-sub ::contents
  attachment-input-signal
  (fn [attachment]
    (-> attachment
        :attachment/contents)))

(rf/reg-sub ::drawing-number
  attachment-input-signal
  (fn [attachment]
    (-> attachment
        :attachment/drawing-number)))

(rf/reg-sub ::creator-name
  attachment-input-signal
  (fn [attachment]
    (-> attachment
        :entity/creator
        user-model/full-name)))

(rf/reg-sub ::read-permissions
  attachment-input-signal
  (fn [attachment]
    (-> attachment
        :entity/required-read-permissions
        set)))

(rf/reg-sub ::visibility-restricted?
  (fn [[_ attachment-id]]
    {:pre [(integer? attachment-id)]}
    (rf/subscribe [::read-permissions attachment-id]))
  (fn [permissions]
    (-> permissions
        :permission.attachment/read-restricted
        boolean)))

(rf/reg-sub ::attachment-types
  attachment-input-signal
  :-> :attachment/types)

(rf/reg-sub ::latest-change
  attachment-input-signal
  :-> :latest-change)

(rf/reg-sub ::locked-for-editing-by
  attachment-input-signal
  :-> :attachment/locked-for-editing-by)

(rf/reg-sub ::versions-for-attachment
  attachment-input-signal
  (fn [attachment]
    (->> attachment
         :attachment/versions
         (sort-by :attachment.version/version >))))

(rf/reg-sub ::non-markup-versions-for-attachment
  (fn [[_ attachment-id]]
    (rf/subscribe [::versions-for-attachment attachment-id]))
  (fn [versions _]
    (->> versions
         (remove :attachment.version/authority-markup?))))

(rf/reg-sub ::latest-version
  (fn [[_ attachment-id]]
    {:pre [(integer? attachment-id)]}
    (rf/subscribe [::versions-for-attachment attachment-id]))
  :-> first)

(rf/reg-sub ::latest-non-markup-version
  (fn [[_ attachment-id]]
    (rf/subscribe [::non-markup-versions-for-attachment attachment-id]))
  :-> first)

(rf/reg-sub ::latest-version-id
  (fn [[_ attachment-id]]
    {:pre [(integer? attachment-id)]}
    (rf/subscribe [::latest-version attachment-id]))
  :-> :db/id)

(rf/reg-sub ::latest-non-markup-version-id
  (fn [[_ attachment-id]]
    (rf/subscribe [::latest-non-markup-version attachment-id]))
  :-> :db/id)

(rf/reg-sub ::latest-file
  (fn [[_ attachment-id]]
    {:pre [(integer? attachment-id)]}
    (rf/subscribe [::latest-version-id attachment-id]))
  (fn [version-id]
    (<sub [::latest-file-for-version version-id])))

(rf/reg-sub ::latest-filename
  (fn [[_ attachment-id]]
    {:pre [(integer? attachment-id)]}
    (rf/subscribe [::latest-file attachment-id]))
  (fn [file]
    (-> file
        :attachment.version.file/filename)))

(rf/reg-sub ::latest-acceptance
  (fn [[_ attachment-id]]
    {:pre [(integer? attachment-id)]}
    (rf/subscribe [::latest-non-markup-version attachment-id]))
  (fn [version]
    (<sub [::acceptance-subs/entity-latest-acceptance (:db/id version)])))

(rf/reg-sub ::latest-acceptance-value
  (fn [[_ attachment-id]]
    {:pre [(integer? attachment-id)]}
    (rf/subscribe [::latest-acceptance attachment-id]))
  (fn [acceptance]
    (-> acceptance
        :acceptance/value)))

(rf/reg-sub ::acceptances-for-attachment-versions
  (fn [[_ attachment-id] _]
    {:pre [(integer? attachment-id)]}
    (rf/subscribe [::versions-for-attachment attachment-id]))
  (fn [versions]
    (let [acceptances (mapcat #(->> %
                                    :entity/_parent
                                    (filter (fn [child]
                                              (eu/is-type? :entity.type/acceptance child))))
                              versions)]
      (->> acceptances
           (sort-by :entity/created)
           reverse))))

;; Permission checks

(rf/reg-sub ::can-delete-attachment?
  (fn [[_ target-entity-id attachment-id]]
    {:pre [(integer? target-entity-id)]}
    (rf/subscribe [::process/signal-permitted? target-entity-id [:process.signal/attachment-delete
                                                                 (util/mmap attachment-id)]]))
  :-> boolean)

(rf/reg-sub ::can-create-acceptance?
  (fn [[_ target-entity-id value acceptance-parent-id]]
    {:pre [(integer? target-entity-id)]}
    (rf/subscribe [::process/signal-permitted?
                   target-entity-id
                   [(acceptance-model/value->signal value false)
                    {:target-id acceptance-parent-id}]]))
  :-> boolean)

(rf/reg-sub ::can-alter-visibility?
  (fn [[_ target-entity-id attachment-id]]
    {:pre [(integer? target-entity-id)]}
    (rf/subscribe [::process/signal-permitted? target-entity-id [:process.signal/attachment-set-visibility-restrictions
                                                                 {:attachment-id attachment-id}]]))
  :-> boolean)
